import { Link } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import { FOOTER_GROUPS } from '../Header/navigation';
import styles from './Footer.module.css';

/**
 * Footer — allineato alla home del riferimento
 * (https://www.infocomuni.it/puglia/, visual-diff 29/07/2026):
 *
 *   contenitore proprio 1200px (PIÙ STRETTO del corpo pagina: è una scelta
 *   d'impaginato del riferimento, riprodotta)
 *   quattro colonne DISUGUALI (~323/274/211/211, gap 44px)
 *     · marchio a immagine + presentazione + CTA outline
 *     · contatti + riga di icone social circolari 34×34
 *     · Esplora        (con chevron ›)
 *     · Istituzionale  (con chevron ›)
 *   firma consorzio: logo ATLAS Gate fra due filetti
 *   barra finale IN COLONNA CENTRATA: copyright · striscia AGID · legali
 *
 * Divergenza dichiarata dal riferimento: il blocco licenze per FONTE resta
 * (KB Arianna/dati.gov.it CC BY 4.0, Wikipedia CC BY-SA). Il riferimento
 * non lo ha, ma è attribuzione richiesta dalle licenze (vincolo 5), non un
 * ornamento: si sposta in coda, compatto, ma non si elimina.
 *
 * Accessibilità: ogni gruppo di link è un `<nav>` con nome proprio; le
 * icone social hanno nome esplicito + avviso di nuova finestra.
 */

/* Icone social del riferimento (percorsi SVG estratti dalla pagina live).
   Le destinazioni sono i profili del consorzio ATLAS Gate. */
const SOCIAL: readonly { nome: string; href: string; path: string }[] = [
  {
    nome: 'Facebook',
    href: 'https://www.facebook.com/atlasgate',
    path: 'M13 22v-8h2.6l.4-3H13V9.3c0-.9.3-1.5 1.6-1.5H16V5.1C15.7 5 14.7 5 13.9 5 11.5 5 10 6.4 10 9v2H7.5v3H10v8h3z',
  },
  {
    nome: 'Instagram',
    href: 'https://www.instagram.com/atlasgate',
    path: 'M12 2.16c3.2 0 3.58.01 4.85.07 1.17.05 1.8.25 2.23.41.56.22.96.48 1.38.9.42.42.68.82.9 1.38.16.42.36 1.06.41 2.23.06 1.27.07 1.65.07 4.85s-.01 3.58-.07 4.85c-.05 1.17-.25 1.8-.41 2.23-.22.56-.48.96-.9 1.38a3.72 3.72 0 0 1-1.38.9c-.42.16-1.06.36-2.23.41-1.27.06-1.65.07-4.85.07s-3.58-.01-4.85-.07c-1.17-.05-1.8-.25-2.23-.41a3.72 3.72 0 0 1-1.38-.9 3.72 3.72 0 0 1-.9-1.38c-.16-.42-.36-1.06-.41-2.23-.06-1.27-.07-1.65-.07-4.85s.01-3.58.07-4.85c.05-1.17.25-1.8.41-2.23.22-.56.48-.96.9-1.38.42-.42.82-.68 1.38-.9.42-.16 1.06-.36 2.23-.41C8.42 2.17 8.8 2.16 12 2.16zm0 4.06a4.16 4.16 0 1 1 0 8.32 4.16 4.16 0 0 1 0-8.32zm5.31-1.84a.97.97 0 1 1-1.94 0 .97.97 0 0 1 1.94 0z',
  },
  {
    nome: 'TikTok',
    href: 'https://www.tiktok.com/@atlasgate',
    path: 'M16.6 5.82A4.28 4.28 0 0 1 15.54 3h-3.09v12.4a2.59 2.59 0 1 1-2.59-2.59c.27 0 .53.04.78.12V9.77a5.76 5.76 0 0 0-.78-.05 5.66 5.66 0 1 0 5.66 5.66V9.01a7.36 7.36 0 0 0 4.3 1.38V7.3a4.34 4.34 0 0 1-3.22-1.48z',
  },
  {
    nome: 'LinkedIn',
    href: 'https://www.linkedin.com/company/atlasgate',
    path: 'M6.94 8.5a1.97 1.97 0 1 1-.01-3.94 1.97 1.97 0 0 1 .01 3.94zM5.34 10.2h3.2V20h-3.2zM10.34 10.2h3.07v1.34h.04c.43-.81 1.47-1.66 3.03-1.66 3.24 0 3.84 2.13 3.84 4.9V20h-3.2v-4.65c0-1.11-.02-2.54-1.55-2.54-1.55 0-1.79 1.21-1.79 2.46V20h-3.2V10.2z',
  },
  {
    nome: 'YouTube',
    href: 'https://www.youtube.com/@atlasgate',
    path: 'M21.6 7.2a2.5 2.5 0 0 0-1.76-1.77C18.27 5 12 5 12 5s-6.27 0-7.84.43A2.5 2.5 0 0 0 2.4 7.2 26.1 26.1 0 0 0 2 12a26.1 26.1 0 0 0 .4 4.8 2.5 2.5 0 0 0 1.76 1.77C5.73 19 12 19 12 19s6.27 0 7.84-.43a2.5 2.5 0 0 0 1.76-1.77A26.1 26.1 0 0 0 22 12a26.1 26.1 0 0 0-.4-4.8zM10 15V9l5.2 3-5.2 3z',
  },
];

/* I gruppi 0 e 1 (Esplora, Istituzionale) sono colonne; il gruppo 2
   (note legali) va nella barra in fondo, come nell'originale. */
const COLUMN_GROUPS = FOOTER_GROUPS.slice(0, 2);
const LEGAL_GROUP = FOOTER_GROUPS[2];

export function Footer() {
  const { t } = useTranslation();
  const year = new Date().getFullYear();
  const newWindow = t('link_new_window', 'apre in nuova finestra');

  return (
    <footer className={styles.footer}>
      <div className={styles.inner}>
        <div className={styles.cols}>
          <section className={styles.brandCol} aria-labelledby="ftr-brand">
            <Link to="/" className={styles.brand}>
              <img
                src="/img/logo-white.svg"
                alt="InfoComuni"
                className={styles.brandLogo}
                width={243}
                height={38}
              />
            </Link>
            {/* `<Trans>` e non una stringa concatenata: il grassetto sta
                DENTRO la frase e in 11 lingue non cade sulle stesse parole. */}
            <p id="ftr-brand" className={styles.lead}>
              <Trans
                i18nKey="top_footer_lead"
                defaults="Piattaforma federata dei <1>257 Comuni della Puglia</1>. Un progetto <1>Rete d'Impresa EPICA</1>."
                components={{ 1: <strong /> }}
              />
            </p>
            <Link to="/rete-civica/attiva-comune/" className={styles.cta}>
              <span className={styles.ctaDot} aria-hidden="true" />
              {t('ftr_attiva_btn', 'Attiva il tuo Comune')}
              <span aria-hidden="true">→</span>
            </Link>
          </section>

          <section className={styles.contactCol} aria-labelledby="ftr-contact">
            <h2 id="ftr-contact" className={styles.groupTitle}>
              {t('ftr_contact', 'Contatti')}
            </h2>
            <address className={styles.address}>
              <strong>{t('top_footer_brindisi_puglia', 'Brindisi, Puglia')}</strong>
              <br />
              {t('top_footer_rete_epica', "Rete d'Impresa EPICA")}
              <br />
              <br />
              <a href="mailto:info@infocomuni.it">info@infocomuni.it</a>
            </address>
            <ul
              className={styles.social}
              role="list"
              aria-label={t('ftr_social_label', 'Profili social')}
            >
              {SOCIAL.map((social) => (
                <li key={social.nome}>
                  <a
                    href={social.href}
                    className={styles.socialLink}
                    data-external="true"
                    rel="noopener noreferrer"
                    aria-label={`${social.nome} (${newWindow})`}
                    title={social.nome}
                  >
                    <svg viewBox="0 0 24 24" width="17" height="17" aria-hidden="true">
                      <path fill="currentColor" d={social.path} />
                    </svg>
                  </a>
                </li>
              ))}
            </ul>
          </section>

          {COLUMN_GROUPS.map((group) => (
            <nav
              key={group.titleKey}
              className={styles.col}
              aria-label={t(group.titleKey, group.titleFallback)}
            >
              <h2 className={styles.groupTitle}>
                {t(group.titleKey, group.titleFallback)}
              </h2>
              <ul className={styles.list}>
                {group.items.map((item) => (
                  <li key={item.to}>
                    <Link to={item.to} className={styles.link}>
                      <span className={styles.chevron} aria-hidden="true">
                        ›
                      </span>
                      {t(item.labelKey, item.fallback)}
                    </Link>
                  </li>
                ))}
              </ul>
            </nav>
          ))}
        </div>

        {/* Le licenze, per FONTE (vincolo 5): divergenza dichiarata dal
            riferimento — vedi il commento in testa al file. */}
        <div className={styles.licenses}>
          <p>
            {t(
              'ftr_licence_datasets',
              'Dati dei comuni: KB Arianna v1 (COOPLY / Rete EPICA) e dati.gov.it',
            )}{' '}
            —{' '}
            <a
              href="https://creativecommons.org/licenses/by/4.0/deed.it"
              className={styles.licenseLink}
              data-external="true"
              rel="license noopener noreferrer"
            >
              CC BY 4.0
            </a>
          </p>
          <p>
            {t('ftr_licence_wikipedia', 'Estratti enciclopedici: Wikipedia')} —{' '}
            <a
              href="https://creativecommons.org/licenses/by-sa/4.0/deed.it"
              className={styles.licenseLink}
              data-external="true"
              rel="license noopener noreferrer"
            >
              CC BY-SA 4.0
            </a>{' '}
            (
            {t(
              'licence_share_alike',
              'licenza diversa: la condivisione richiede la stessa licenza',
            )}
            )
          </p>
        </div>

        {/* Firma consorzio: logo centrato fra due filetti. */}
        <div className={styles.sign}>
          <span className={styles.signLine} aria-hidden="true" />
          <img
            src="/img/logo-atlasgate-white.svg"
            alt={t('ftr_consorzio_alt', 'Consorzio ATLAS Gate')}
            className={styles.signLogo}
            width={220}
            height={75}
          />
          <span className={styles.signLine} aria-hidden="true" />
        </div>

        <div className={styles.bar}>
          <p className={styles.copy}>
            © {year} <strong>{t('ftr_rete', "Rete d'Impresa EPICA")}</strong>
          </p>

          {/* Striscia AGID: card a piena larghezza, come il riferimento. */}
          <a
            href="https://www.dati.gov.it/casi-di-riuso-mosaico"
            className={styles.agid}
            data-external="true"
            rel="noopener noreferrer"
          >
            <svg width="26" height="26" viewBox="0 0 34 34" fill="none" aria-hidden="true">
              <g stroke="currentColor" strokeWidth="1.4" opacity="0.8">
                <path d="M17 6 6 13M17 6l11 7M6 13v9M28 13v9M6 22l11 6M28 22l-11 6M17 6v11M6 13l11 4M28 13l-11 4" />
              </g>
              <g fill="currentColor">
                <circle cx="17" cy="6" r="2.3" />
                <circle cx="6" cy="13" r="2.3" />
                <circle cx="28" cy="13" r="2.3" />
                <circle cx="6" cy="22" r="2.3" />
                <circle cx="28" cy="22" r="2.3" />
                <circle cx="17" cy="28" r="2.3" />
                <circle cx="17" cy="17" r="2.6" />
              </g>
            </svg>
            <b>{t('ftr_agid_selected', 'Selezionato da AGID')}</b>
            <span className={styles.agidCase}>
              {t('ftr_agid_case', 'dati.gov.it · Caso di riutilizzo')}
            </span>
            <span className={styles.agidCta}>
              {t('ftr_agid_cta', 'Vedi su dati.gov.it')} <span aria-hidden="true">→</span>
            </span>
          </a>

          {LEGAL_GROUP ? (
            <nav aria-label={t(LEGAL_GROUP.titleKey, LEGAL_GROUP.titleFallback)}>
              <ul className={styles.legal}>
                {LEGAL_GROUP.items.map((item) => (
                  <li key={item.to}>
                    <Link to={item.to} className={styles.legalLink}>
                      {t(item.labelKey, item.fallback)}
                    </Link>
                  </li>
                ))}
              </ul>
            </nav>
          ) : null}
        </div>
      </div>
    </footer>
  );
}
